export interface GeoCoords {
	latitude: number;
	longitude: number;
	accuracy: number;
}

export interface GeoLocationOptions {
	timeoutMs?: number;
	maximumAgeMs?: number;
}

export interface GeoLocationTrackerOptions extends GeoLocationOptions {
	intervalMs?: number;
	durationMs?: number;
	onPosition: (coords: GeoCoords) => Promise<boolean | void> | boolean | void;
	onError?: (error: unknown) => void;
	onStop?: () => void;
}

/**
 * Thin wrapper around navigator.geolocation. Works inside the Capacitor WebView as long as
 * the native location permissions are declared (Info.plist / AndroidManifest). If we need
 * native-only behavior later we can swap to @capacitor/geolocation without touching callers.
 */
const useGeolocation = () => {
	const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

	const getCurrentPosition = (timeoutMsOrOptions: number | GeoLocationOptions = 8000): Promise<GeoCoords | null> => {
		if (!isSupported) {
			return Promise.resolve(null);
		}

		const options: GeoLocationOptions = typeof timeoutMsOrOptions === 'number' ? { timeoutMs: timeoutMsOrOptions } : timeoutMsOrOptions;
		const timeoutMs = options.timeoutMs ?? 8000;
		const maximumAgeMs = options.maximumAgeMs ?? 30000;

		const requestPosition = (positionOptions: PositionOptions): Promise<GeoCoords | null> =>
			new Promise((resolve) => {
				navigator.geolocation.getCurrentPosition(
					(position) =>
						resolve({
							latitude: position.coords.latitude,
							longitude: position.coords.longitude,
							accuracy: Math.round(position.coords.accuracy ?? 0),
						}),
					(error) => {
						console.warn('geolocation failed', error);
						resolve(null);
					},
					positionOptions,
				);
			});

		return requestPosition({
			enableHighAccuracy: false,
			timeout: Math.min(timeoutMs, 1500),
			maximumAge: maximumAgeMs,
		}).then(
			(cachedPosition) =>
				cachedPosition ??
				requestPosition({
					enableHighAccuracy: true,
					timeout: timeoutMs,
					maximumAge: maximumAgeMs,
				}),
		);
	};

	const startForegroundTracker = (options: GeoLocationTrackerOptions): (() => void) => {
		if (!isSupported || typeof window === 'undefined') {
			options.onStop?.();
			return () => undefined;
		}

		const intervalMs = options.intervalMs ?? 5000;
		const durationMs = options.durationMs ?? 10 * 60 * 1000;
		const startedAt = Date.now();
		let timeoutId: number | undefined;
		let stopped = false;

		const stop = () => {
			if (stopped) {
				return;
			}

			stopped = true;
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
			options.onStop?.();
		};

		const schedule = () => {
			if (stopped) {
				return;
			}

			if (Date.now() - startedAt >= durationMs) {
				stop();
				return;
			}

			timeoutId = window.setTimeout(tick, intervalMs);
		};

		const tick = async () => {
			if (stopped) {
				return;
			}

			try {
				const coords = await getCurrentPosition({
					timeoutMs: options.timeoutMs ?? 10000,
					maximumAgeMs: options.maximumAgeMs ?? 0,
				});

				if (coords) {
					const keepTracking = await options.onPosition(coords);
					if (keepTracking === false) {
						stop();
						return;
					}
				}
			} catch (error) {
				options.onError?.(error);
			}

			schedule();
		};

		void tick();

		return stop;
	};

	return {
		isSupported,
		getCurrentPosition,
		startForegroundTracker,
	};
};

export default useGeolocation;
